import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/courses/RichTextEditor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Trash2, Edit, Save, X, BookOpen, Layers, Video, ChevronDown, ChevronUp, Upload, Image } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { Database } from "@/integrations/supabase/types";

type CourseStatus = Database["public"]["Enums"]["course_status"];
type LessonType = Database["public"]["Enums"]["lesson_type"];

interface Course {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  thumbnail_url: string | null;
  price: number | null;
  currency: string | null;
  level: string | null;
  status: CourseStatus | null;
  duration_hours: number | null;
  instructor_id: string;
}

interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  order_index: number;
}

interface Lesson {
  id: string;
  module_id: string;
  title: string;
  description: string | null;
  lesson_type: LessonType | null;
  video_url: string | null;
  content: string | null;
  duration_minutes: number | null;
  order_index: number;
  is_preview: boolean | null;
}

const AdminCourses = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  const [courses, setCourses] = useState<Course[]>([]);
  const [modules, setModules] = useState<Record<string, Module[]>>({});
  const [lessons, setLessons] = useState<Record<string, Lesson[]>>({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Course form state
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [courseForm, setCourseForm] = useState({
    title: "",
    slug: "",
    description: "",
    price: "0",
    currency: "EUR",
    level: "beginner",
    status: "draft" as CourseStatus,
    duration_hours: "",
    thumbnail_url: "",
  });
  
  // Module form state
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [moduleFormCourseId, setModuleFormCourseId] = useState<string | null>(null);
  const [moduleForm, setModuleForm] = useState({
    title: "",
    description: "",
  });
  
  // Lesson form state
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [lessonFormModuleId, setLessonFormModuleId] = useState<string | null>(null);
  const [lessonForm, setLessonForm] = useState({
    title: "",
    description: "",
    lesson_type: "video" as LessonType,
    video_url: "",
    content: "",
    duration_minutes: "",
    is_preview: false,
  });
  
  // UI state
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
      if (!data) {
        toast({
          title: "Access Denied",
          description: "You need admin privileges to access this page.",
          variant: "destructive",
        });
        navigate("/");
      }
    };

    checkAdminRole();
  }, [user, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchCourses();
    }
  }, [isAdmin]);

  const fetchCourses = async () => {
    try {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load courses",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async (courseId: string) => {
    try {
      const { data, error } = await supabase
        .from("modules")
        .select("*")
        .eq("course_id", courseId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setModules(prev => ({ ...prev, [courseId]: data || [] }));
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const fetchLessons = async (moduleId: string) => {
    try {
      const { data, error } = await supabase
        .from("lessons")
        .select("*")
        .eq("module_id", moduleId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setLessons(prev => ({ ...prev, [moduleId]: data || [] }));
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const toggleCourseExpand = (courseId: string) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(courseId)) {
      newExpanded.delete(courseId);
    } else {
      newExpanded.add(courseId);
      if (!modules[courseId]) {
        fetchModules(courseId);
      }
    }
    setExpandedCourses(newExpanded);
  };

  const toggleModuleExpand = (moduleId: string) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
      if (!lessons[moduleId]) {
        fetchLessons(moduleId);
      }
    }
    setExpandedModules(newExpanded);
  };

  // Course CRUD
  const handleCourseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const courseData = {
      title: courseForm.title,
      slug: courseForm.slug || courseForm.title.toLowerCase().replace(/\s+/g, "-"),
      description: courseForm.description || null,
      price: parseFloat(courseForm.price) || 0,
      currency: courseForm.currency,
      level: courseForm.level,
      status: courseForm.status,
      duration_hours: courseForm.duration_hours ? parseInt(courseForm.duration_hours) : null,
      thumbnail_url: courseForm.thumbnail_url || null,
      instructor_id: user.id,
    };

    try {
      if (editingCourseId) {
        const { error } = await supabase
          .from("courses")
          .update(courseData)
          .eq("id", editingCourseId);
        if (error) throw error;
        toast({ title: "Success", description: "Course updated" });
      } else {
        const { error } = await supabase
          .from("courses")
          .insert([courseData]);
        if (error) throw error;
        toast({ title: "Success", description: "Course created" });
      }
      resetCourseForm();
      fetchCourses();
    } catch (error: any) {
      console.error("Course operation error:", error);
      toast({ title: "Error", description: "Failed to save course. Please try again.", variant: "destructive" });
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourseId(course.id);
    setCourseForm({
      title: course.title,
      slug: course.slug,
      description: course.description || "",
      price: course.price?.toString() || "0",
      currency: course.currency || "EUR",
      level: course.level || "beginner",
      status: course.status || "draft",
      duration_hours: course.duration_hours?.toString() || "",
      thumbnail_url: course.thumbnail_url || "",
    });
  };

  const handleDeleteCourse = async (id: string) => {
    if (!confirm("Delete this course and all its content?")) return;
    try {
      const { error } = await supabase.from("courses").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Course deleted" });
      fetchCourses();
    } catch (error: any) {
      console.error("Delete course error:", error);
      toast({ title: "Error", description: "Failed to delete course. Please try again.", variant: "destructive" });
    }
  };

  const resetCourseForm = () => {
    setEditingCourseId(null);
    setCourseForm({
      title: "",
      slug: "",
      description: "",
      price: "0",
      currency: "EUR",
      level: "beginner",
      status: "draft",
      duration_hours: "",
      thumbnail_url: "",
    });
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('course-thumbnails')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('course-thumbnails')
        .getPublicUrl(fileName);

      setCourseForm({ ...courseForm, thumbnail_url: urlData.publicUrl });
      toast({ title: t("admin.thumbnailUploaded") || "Thumbnail uploaded" });
    } catch (error: any) {
      console.error("Thumbnail upload error:", error);
      toast({ title: "Error", description: "Failed to upload thumbnail. Please try again.", variant: "destructive" });
    } finally {
      setUploadingThumbnail(false);
    }
  };

  // Module CRUD
  const handleModuleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moduleFormCourseId) return;

    const existingModules = modules[moduleFormCourseId] || [];
    const moduleData = {
      course_id: moduleFormCourseId,
      title: moduleForm.title,
      description: moduleForm.description || null,
      order_index: editingModuleId 
        ? existingModules.find(m => m.id === editingModuleId)?.order_index || 0
        : existingModules.length,
    };

    try {
      if (editingModuleId) {
        const { error } = await supabase
          .from("modules")
          .update(moduleData)
          .eq("id", editingModuleId);
        if (error) throw error;
        toast({ title: "Success", description: "Module updated" });
      } else {
        const { error } = await supabase
          .from("modules")
          .insert([moduleData]);
        if (error) throw error;
        toast({ title: "Success", description: "Module created" });
      }
      resetModuleForm();
      fetchModules(moduleFormCourseId);
    } catch (error: any) {
      console.error("Module operation error:", error);
      toast({ title: "Error", description: "Failed to save module. Please try again.", variant: "destructive" });
    }
  };

  const handleEditModule = (module: Module) => {
    setEditingModuleId(module.id);
    setModuleFormCourseId(module.course_id);
    setModuleForm({
      title: module.title,
      description: module.description || "",
    });
  };

  const handleDeleteModule = async (id: string, courseId: string) => {
    if (!confirm("Delete this module and all its lessons?")) return;
    try {
      const { error } = await supabase.from("modules").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Module deleted" });
      fetchModules(courseId);
    } catch (error: any) {
      console.error("Delete module error:", error);
      toast({ title: "Error", description: "Failed to delete module. Please try again.", variant: "destructive" });
    }
  };

  const resetModuleForm = () => {
    setEditingModuleId(null);
    setModuleFormCourseId(null);
    setModuleForm({ title: "", description: "" });
  };

  const startAddModule = (courseId: string) => {
    resetModuleForm();
    setModuleFormCourseId(courseId);
  };

  // Lesson CRUD
  const handleLessonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lessonFormModuleId) return;

    const existingLessons = lessons[lessonFormModuleId] || [];
    const lessonData = {
      module_id: lessonFormModuleId,
      title: lessonForm.title,
      description: lessonForm.description || null,
      lesson_type: lessonForm.lesson_type,
      video_url: lessonForm.video_url || null,
      content: lessonForm.content || null,
      duration_minutes: lessonForm.duration_minutes ? parseInt(lessonForm.duration_minutes) : null,
      is_preview: lessonForm.is_preview,
      order_index: editingLessonId
        ? existingLessons.find(l => l.id === editingLessonId)?.order_index || 0
        : existingLessons.length,
    };

    try {
      if (editingLessonId) {
        const { error } = await supabase
          .from("lessons")
          .update(lessonData)
          .eq("id", editingLessonId);
        if (error) throw error;
        toast({ title: "Success", description: "Lesson updated" });
      } else {
        const { error } = await supabase
          .from("lessons")
          .insert([lessonData]);
        if (error) throw error;
        toast({ title: "Success", description: "Lesson created" });
      }
      resetLessonForm();
      fetchLessons(lessonFormModuleId);
    } catch (error: any) {
      console.error("Lesson operation error:", error);
      toast({ title: "Error", description: "Failed to save lesson. Please try again.", variant: "destructive" });
    }
  };

  const handleEditLesson = (lesson: Lesson) => {
    setEditingLessonId(lesson.id);
    setLessonFormModuleId(lesson.module_id);
    setLessonForm({
      title: lesson.title,
      description: lesson.description || "",
      lesson_type: lesson.lesson_type || "video",
      video_url: lesson.video_url || "",
      content: lesson.content || "",
      duration_minutes: lesson.duration_minutes?.toString() || "",
      is_preview: lesson.is_preview || false,
    });
  };

  const handleDeleteLesson = async (id: string, moduleId: string) => {
    if (!confirm("Delete this lesson?")) return;
    try {
      const { error } = await supabase.from("lessons").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Lesson deleted" });
      fetchLessons(moduleId);
    } catch (error: any) {
      console.error("Delete lesson error:", error);
      toast({ title: "Error", description: "Failed to delete lesson. Please try again.", variant: "destructive" });
    }
  };

  const resetLessonForm = () => {
    setEditingLessonId(null);
    setLessonFormModuleId(null);
    setLessonForm({
      title: "",
      description: "",
      lesson_type: "video",
      video_url: "",
      content: "",
      duration_minutes: "",
      is_preview: false,
    });
  };

  const startAddLesson = (moduleId: string) => {
    resetLessonForm();
    setLessonFormModuleId(moduleId);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-6 overflow-y-auto">
      <div className="container mx-auto max-w-6xl pb-12">
        <button
          onClick={() => navigate("/admin")}
          className="mb-6 group flex items-center gap-2 text-foreground/60 hover:text-foreground transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>{t("admin.backToAdmin")}</span>
        </button>

        <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
          {t("admin.courseMgmt")}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Course Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {editingCourseId ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {editingCourseId ? t("admin.editCourse") : t("admin.addCourse")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCourseSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("admin.courseTitle")}</Label>
                  <Input
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.courseSlug")}</Label>
                  <Input
                    value={courseForm.slug}
                    onChange={(e) => setCourseForm({ ...courseForm, slug: e.target.value })}
                    placeholder="auto-generated-from-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.courseDescription")}</Label>
                  <Textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("admin.coursePrice")}</Label>
                    <Input
                      type="number"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.courseCurrency")}</Label>
                    <Select
                      value={courseForm.currency}
                      onValueChange={(v) => setCourseForm({ ...courseForm, currency: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("admin.courseLevel")}</Label>
                    <Select
                      value={courseForm.level}
                      onValueChange={(v) => setCourseForm({ ...courseForm, level: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">{t("courses.beginner")}</SelectItem>
                        <SelectItem value="intermediate">{t("courses.intermediate")}</SelectItem>
                        <SelectItem value="advanced">{t("courses.advanced")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.courseStatus")}</Label>
                    <Select
                      value={courseForm.status}
                      onValueChange={(v) => setCourseForm({ ...courseForm, status: v as CourseStatus })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">{t("admin.draft")}</SelectItem>
                        <SelectItem value="published">{t("admin.published")}</SelectItem>
                        <SelectItem value="archived">{t("admin.archived")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.courseDuration")}</Label>
                  <Input
                    type="number"
                    value={courseForm.duration_hours}
                    onChange={(e) => setCourseForm({ ...courseForm, duration_hours: e.target.value })}
                    placeholder="Hours"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.courseThumbnail")}</Label>
                  {courseForm.thumbnail_url ? (
                    <div className="relative">
                      <img 
                        src={courseForm.thumbnail_url} 
                        alt="Course thumbnail" 
                        className="w-full h-40 object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setCourseForm({ ...courseForm, thumbnail_url: "" })}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingThumbnail ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        ) : (
                          <>
                            <Image className="w-8 h-8 mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">{t("admin.uploadThumbnail")}</p>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        disabled={uploadingThumbnail}
                      />
                    </label>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    {editingCourseId ? t("admin.updateCourse") : t("admin.createCourse")}
                  </Button>
                  {editingCourseId && (
                    <Button type="button" variant="outline" onClick={resetCourseForm}>
                      <X className="w-4 h-4 mr-2" />
                      {t("admin.cancel")}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Module Form */}
          {moduleFormCourseId && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  {editingModuleId ? t("admin.editModule") : t("admin.addModule")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleModuleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("admin.moduleTitle")}</Label>
                    <Input
                      value={moduleForm.title}
                      onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.moduleDescription")}</Label>
                    <Textarea
                      value={moduleForm.description}
                      onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">
                      <Save className="w-4 h-4 mr-2" />
                      {editingModuleId ? t("admin.updateModule") : t("admin.createModule")}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetModuleForm}>
                      <X className="w-4 h-4 mr-2" />
                      {t("admin.cancel")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Lesson Form */}
          {lessonFormModuleId && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  {editingLessonId ? t("admin.editLesson") : t("admin.addLesson")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLessonSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("admin.lessonTitle")}</Label>
                    <Input
                      value={lessonForm.title}
                      onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.lessonDescription")}</Label>
                    <Textarea
                      value={lessonForm.description}
                      onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("admin.lessonType")}</Label>
                      <Select
                        value={lessonForm.lesson_type}
                        onValueChange={(v) => setLessonForm({ ...lessonForm, lesson_type: v as LessonType })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="video">{t("admin.videoLesson")}</SelectItem>
                          <SelectItem value="text">{t("admin.textLesson")}</SelectItem>
                          <SelectItem value="quiz">{t("admin.quizLesson")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t("admin.lessonDuration")}</Label>
                      <Input
                        type="number"
                        value={lessonForm.duration_minutes}
                        onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })}
                        placeholder="Minutes"
                      />
                    </div>
                  </div>
                  {lessonForm.lesson_type === "video" && (
                    <div className="space-y-2">
                      <Label>{t("admin.videoUrl")}</Label>
                      <Input
                        value={lessonForm.video_url}
                        onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>{t("admin.lessonContent")}</Label>
                    <RichTextEditor
                      value={lessonForm.content}
                      onChange={(content) => setLessonForm({ ...lessonForm, content })}
                      placeholder={t("admin.contentPlaceholder") || "Add lesson content..."}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_preview"
                      checked={lessonForm.is_preview}
                      onChange={(e) => setLessonForm({ ...lessonForm, is_preview: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_preview">{t("admin.freePreview")}</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">
                      <Save className="w-4 h-4 mr-2" />
                      {editingLessonId ? t("admin.updateLesson") : t("admin.createLesson")}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetLessonForm}>
                      <X className="w-4 h-4 mr-2" />
                      {t("admin.cancel")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Courses List */}
        <div className="mt-8 space-y-4">
          <h2 className="text-2xl font-bold">{t("admin.existingCourses")} ({courses.length})</h2>
          {courses.map((course) => (
            <Card key={course.id} className="overflow-hidden">
              <Collapsible open={expandedCourses.has(course.id)}>
                <CollapsibleTrigger asChild>
                  <CardHeader 
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleCourseExpand(course.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-5 h-5 text-primary" />
                        <div>
                          <CardTitle className="text-lg">{course.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">{course.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={course.status === "published" ? "default" : "secondary"}>
                          {course.status}
                        </Badge>
                        <Badge variant="outline">{course.level}</Badge>
                        <Badge variant="outline">{course.price} {course.currency}</Badge>
                        {expandedCourses.has(course.id) ? <ChevronUp /> : <ChevronDown />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="border-t">
                    <div className="flex gap-2 mb-4">
                      <Button size="sm" variant="outline" onClick={() => handleEditCourse(course)}>
                        <Edit className="w-4 h-4 mr-1" /> {t("admin.edit")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => startAddModule(course.id)}>
                        <Plus className="w-4 h-4 mr-1" /> {t("admin.addModule")}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteCourse(course.id)}>
                        <Trash2 className="w-4 h-4 mr-1" /> {t("admin.delete")}
                      </Button>
                    </div>

                    {/* Modules */}
                    <div className="space-y-2">
                      {(modules[course.id] || []).map((module) => (
                        <Card key={module.id} className="bg-muted/30">
                          <Collapsible open={expandedModules.has(module.id)}>
                            <CollapsibleTrigger asChild>
                              <CardHeader 
                                className="py-3 cursor-pointer hover:bg-muted/50"
                                onClick={() => toggleModuleExpand(module.id)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Layers className="w-4 h-4 text-secondary" />
                                    <span className="font-medium">{module.order_index + 1}. {module.title}</span>
                                  </div>
                                  {expandedModules.has(module.id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </div>
                              </CardHeader>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <CardContent className="pt-0">
                                <div className="flex gap-2 mb-3">
                                  <Button size="sm" variant="ghost" onClick={() => handleEditModule(module)}>
                                    <Edit className="w-3 h-3 mr-1" /> {t("admin.edit")}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => startAddLesson(module.id)}>
                                    <Plus className="w-3 h-3 mr-1" /> {t("admin.addLesson")}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteModule(module.id, course.id)}>
                                    <Trash2 className="w-3 h-3 mr-1" /> {t("admin.delete")}
                                  </Button>
                                </div>

                                {/* Lessons */}
                                <div className="space-y-1 ml-4">
                                  {(lessons[module.id] || []).map((lesson) => (
                                    <div key={lesson.id} className="flex items-center justify-between p-2 rounded bg-background/50">
                                      <div className="flex items-center gap-2">
                                        <Video className="w-3 h-3 text-accent" />
                                        <span className="text-sm">{lesson.order_index + 1}. {lesson.title}</span>
                                        {lesson.is_preview && <Badge variant="outline" className="text-xs">Preview</Badge>}
                                        <Badge variant="secondary" className="text-xs">{lesson.lesson_type}</Badge>
                                        {lesson.duration_minutes && (
                                          <span className="text-xs text-muted-foreground">{lesson.duration_minutes} min</span>
                                        )}
                                      </div>
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => handleEditLesson(lesson)}>
                                          <Edit className="w-3 h-3" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive" onClick={() => handleDeleteLesson(lesson.id, module.id)}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </CollapsibleContent>
                          </Collapsible>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminCourses;
